import * as XLSX from "xlsx";
import {
  findCellWithValueInRange,
  getMergedCellValue,
} from "../../../../../app/utils/process.excel.helper";
import { isEmpty } from "../../../utils/helpers";

// Shared showSnackbar function reference
let showSnackbar = null;
let maxRow = null;
let maxCol = null;
let worksheet = null;

const ASSESSMENT_NAME = "Assessment Name";
const ERRORS = {
  MISSING_ASSESSMENT_NAME: `The '${ASSESSMENT_NAME}' row or row label is missing from the 'SOE' sheet.`,
  INVALID_FORMAT_WARNING: (warnings) => 
    `Found ${warnings.length} cell(s) with invalid format:\n${warnings.slice(0, 5).join('\n')}${warnings.length > 5 ? '\n...' : ''}\n Expected formats are "U[number]", "NU[number]", or "[number]".`,
  INVALID_CELL_FORMAT: (cellAddress, multiplierValue) =>
    `  • ${cellAddress} = "${multiplierValue}"`,
  VISITS_OR_ASSESSMENTS_MISSING_WARNING: (type) =>
    `Only exact visit and assessment name matches that exist in the SOE will be uploaded.
    \r\nYour upload contains visit or assessment names that do not exist in the SOE.
    \r\nThe SOE panel upload requires exact visit and assessment name matches and no added visits or assessments.`,
};

// Main processing function
export const processSoeData = (
  soeWorksheet,
  showSnackbarFn,
  studyId,
  scheduleId,
  scheduleOfEventsAssessments,
  scheduleOfEventsVisits,
) => {
  const errors = [];
  const warnings = [];

  showSnackbar = showSnackbarFn;
  worksheet = soeWorksheet;

  // Get sheet dimensions
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  maxRow = range.e.r;
  maxCol = range.e.c;

  try {
    const assessmentFlagCell = getAssessmentFlagCell(errors);
    
    // Check if there were errors during assessment flag cell search
    if (errors.length > 0) {
      throw errors[0];
    }
    
    const {
      gridData,
      sheetVisitNames,
      sheetAssessmentNames,
    } = getSOEGridData(worksheet, assessmentFlagCell, studyId, scheduleId, warnings);
    
    if (warnings.length > 0) {
      showSnackbar(ERRORS.INVALID_FORMAT_WARNING(warnings), "warning");
    }

    matchVisitAndAssessmentNames(
      sheetVisitNames,
      scheduleOfEventsVisits,
      sheetAssessmentNames,
      scheduleOfEventsAssessments,
    );
    
    return gridData;
  } catch (error) {
    // Only handle our predefined business logic errors
    if (error instanceof SoeTableUploadError) {
      return { error: error.message };
    }
    // For unexpected system errors, re-throw to be handled at a higher level
    throw error;
  }
};

// Find the "Assessment Name" cell, checking all cells in first few columns
const getAssessmentFlagCell = (errors = []) => {
  const visitCell = findCellWithValueInRange(
    worksheet,
    ASSESSMENT_NAME,
    0,
    maxRow,
    0,
    maxCol,
  );
  if (!visitCell) {
    showSnackbar(
      ERRORS.MISSING_ASSESSMENT_NAME,
      "error",
    );
    errors.push(new SoeTableUploadError(ERRORS.MISSING_ASSESSMENT_NAME));
    return null;
  }
  return visitCell;
};

// Get visit names starting from column E (index 4)
const getSOEGridData = (
  worksheet,
  cell, 
  studyId,
  scheduleId,
  warnings,
) => {
  if (!cell) {
    return {
      gridData: [],
      sheetVisitNames: new Set(),
      sheetAssessmentNames: new Set(),
    };
  }

  const gridData = [];
  const sheetVisitNames = new Set();
  const sheetAssessmentNames = new Set();

  for (let row = cell.row + 1; row <= maxRow; row++) {
    const assessmentCellAddress = XLSX.utils.encode_cell({ r: row, c: cell.col });
    const assessmentName = getMergedCellValue(worksheet, assessmentCellAddress);
    sheetAssessmentNames.add(assessmentName);
    for (let col = cell.col + 1; col <= maxCol; col++) {
      const visitCellAddress = XLSX.utils.encode_cell({ r: cell.row, c: col });
      const visitName = getMergedCellValue(worksheet, visitCellAddress);
      sheetVisitNames.add(visitName);
      const multiplierCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const multiplierValue = getMergedCellValue(worksheet, multiplierCellAddress);
      const {
        uniqueNonUniqueForm,
        multiplier,
        mappingAssessmentVisit,
        valid,
      } = mapGridCell(multiplierValue, multiplierCellAddress, warnings);

      if (valid) {
        gridData.push({
          studyId: studyId,
          scheduleOfEventsId: scheduleId,
          scheduleOfEventsAssessmentName: assessmentName,
          scheduleOfEventsVisitName: visitName,
          multiplier,
          uniqueNonUniqueForm,
          mappingAssessmentVisit,
        });
      }
    }
  }

  return {
    gridData,
    sheetVisitNames,
    sheetAssessmentNames,
  };
};

const mapGridCell = (multiplierValue, cellAddress, warnings) => {
  if (multiplierValue === 0 || isEmpty(multiplierValue)) {
    return {
      uniqueNonUniqueForm: "",
      multiplier: "",
      mappingAssessmentVisit: 0,
      valid: true,
    };
  }

  // Validate format: Expected format is "U[number]", "NU[number]", "[number]", "U", or "NU"
  // Examples: "U[1]", "NU[2]", "u[3]", "nu[4]", "[1]", "[5]", "[1.5]", "[1,5]", "U", "NU"
  // Accepts integers and decimals with dot or comma (case-insensitive)
  const formatRegex = /^(NU|U)(\s*\[\s*\d+([.,]\d+)?\s*\])?$|^\[\s*\d+([.,]\d+)?\s*\]$/i;
  
  if (!formatRegex.test(multiplierValue?.trim())) {
    // Invalid format - add warning and return empty values
    warnings.push(ERRORS.INVALID_CELL_FORMAT(cellAddress, multiplierValue));
    return {
      uniqueNonUniqueForm: "",
      multiplier: "",
      mappingAssessmentVisit: 0,
      valid: false,
    };
  }

  const splittedValue = multiplierValue?.split("[");
  let uniqueNonUniqueForm = splittedValue?.[0]?.trim() || "";
  
  // If no prefix (only [number]), uniqueNonUniqueForm is empty
  if (!isEmpty(uniqueNonUniqueForm)) {
    uniqueNonUniqueForm = uniqueNonUniqueForm.toUpperCase() === "NU" ? "N" : uniqueNonUniqueForm.toUpperCase();
  }
  
  let multiplier = splittedValue?.[1]?.replace("]", "")?.trim() || "";
  // Replace comma with dot for decimal parsing
  multiplier = multiplier?.replace(",", ".");
  multiplier = isEmpty(multiplier) || isNaN(multiplier) ? "1" : multiplier;
  const mappingAssessmentVisit = (Number(multiplier) > 0) ? 1 : 0;

  return {
    uniqueNonUniqueForm,
    multiplier,
    mappingAssessmentVisit,
    valid: true,
  };
};

const matchVisitAndAssessmentNames = (
  sheetVisitNames,
  scheduleOfEventsVisits,
  sheetAssessmentNames,
  scheduleOfEventsAssessments,
) => {
  // Helper function to find mismatches using case-insensitive comparison
  const findMismatches = (sheetNames, existingNames) => {
    if (!existingNames?.length) return Array.from(sheetNames);
    
    const normalizedExisting = new Set(existingNames.map(name => name.toLowerCase()));
    return [...sheetNames].filter(name => !normalizedExisting.has(name.toLowerCase()));
  };

  // Extract names from data
  const visitNames = scheduleOfEventsVisits?.["study"]?.map(v => v.visitName) || [];
  const assessmentNames = scheduleOfEventsAssessments?.["study"]?.map(
    a => `${a.assessmentType} - ${a.assessmentName}`
  ) || [];

  // Find mismatches
  const unmatchedVisits = findMismatches(sheetVisitNames, visitNames);
  const unmatchedAssessments = findMismatches(sheetAssessmentNames, assessmentNames);

  if (unmatchedVisits.length > 0 || unmatchedAssessments.length > 0) {
    showSnackbar(ERRORS.VISITS_OR_ASSESSMENTS_MISSING_WARNING(), "error"); 
    throw new SoeTableUploadError(ERRORS.VISITS_OR_ASSESSMENTS_MISSING_WARNING());
  }
};

// Custom error class for predefined business logic errors
class SoeTableUploadError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SoeTableUploadError';
  }
};
